import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  getProjectDataSources,
  getProjectDataSourcesAreLoading,
  getProjectPage,
} from "../../../ducks/project/selectors";

import { formatDataSourceCondition } from "../utils";
import { setProjectDataSources, setProjectDataSourcesAreLoading } from "../../../ducks/project/project";
import { useDataSources } from "./useDataSources";
import { useWidgetConfiguration } from "./useWidgetConfiguration";
import { useDataSourceCondition } from "../../../hooks/layer/useDataSourceCondition";
import { useEwktGeometry } from "../../FeatureSelector/hooks/useEwktGeometry";
import { useIsConfiguration } from "../../../hooks/location/useIsConfiguration";

import { FilterConditionOperation } from "../../../components/LayerFilter/types";
import {
  ConfigDataSource,
  ConfigFilter,
  DataSourcePromise,
  SelectedFilters,
  WidgetType,
} from "../../FeatureCard/types";

// eslint-disable-next-line max-lines-per-function
export const useProjectDataSources = (filters: SelectedFilters) => {
  const dispatch = useDispatch();
  const projectDataSources = useSelector(getProjectDataSources);
  const isLoading = useSelector(getProjectDataSourcesAreLoading);
  const page = useSelector(getProjectPage);
  const ewktGeometry = useEwktGeometry();
  const getDashboardCondition = useDataSourceCondition();
  const isConfiguration = useIsConfiguration();
  const { currentConfig } = useWidgetConfiguration(WidgetType.Dashboard);

  const filtersDeps = useMemo(() => JSON.stringify(filters), [filters]);
  const prevFilters = useRef(filters);

  const dataSources: ConfigDataSource[] = currentConfig?.dataSources ?? [];
  const configFilters: ConfigFilter[] = currentConfig?.filters;

  const { getDataSourcePromises, getUpdatingDataSources, getUpdatedDataSources, zoomToLayersExtent } = useDataSources({
    filters,
    config: currentConfig,
  });

  const fetchData = useCallback(
    (fetchingDataSources?: ConfigDataSource[]) => {
      const currentDataSources = (fetchingDataSources || dataSources)?.filter(({ condition }) => {
        return (
          !condition ||
          formatDataSourceCondition({
            condition,
            configFilters,
            filters,
            geometry: ewktGeometry,
          })
        );
      });

      if (!currentDataSources?.length) return;

      dispatch(setProjectDataSourcesAreLoading(true));

      const otherDataSources =
        projectDataSources?.length && currentDataSources?.length
          ? projectDataSources?.filter(({ name }) => !currentDataSources.some(item => item.name === name))
          : [];

      if (otherDataSources.length) {
        dispatch(setProjectDataSources(otherDataSources));
      }

      const notGeometryFilters = { ...filters, geometry: undefined };
      const prevNotGeometryFilters = { ...prevFilters.current, geometry: undefined };

      Promise.allSettled(currentDataSources.map(getDataSourcePromises))
        .then((responses: DataSourcePromise[]) => {
          const responseDataSources = getUpdatedDataSources(responses, currentDataSources, otherDataSources);

          dispatch(setProjectDataSources(responseDataSources));

          if (JSON.stringify(notGeometryFilters) === JSON.stringify(prevNotGeometryFilters)) return;

          const zoomToLayers = currentConfig.layers?.filter(({ filterZoomTo }) => filterZoomTo);

          if (!zoomToLayers?.length) return;

          zoomToLayersExtent(
            zoomToLayers.map(({ name }) => ({
              layerName: name,
              query: getDashboardCondition(name).join(` ${FilterConditionOperation.AND} `),
            })),
          );
        })
        .finally(() => {
          dispatch(setProjectDataSourcesAreLoading(false));
        });
    },
    [
      dataSources,
      dispatch,
      projectDataSources,
      filters,
      getDataSourcePromises,
      configFilters,
      ewktGeometry,
      getUpdatedDataSources,
      currentConfig.layers,
      zoomToLayersExtent,
      getDashboardCondition,
    ],
  );

  const filterData = useCallback(() => {
    if (isLoading) return;

    const isFilterChanged = JSON.stringify(prevFilters.current) !== filtersDeps;

    if (!isFilterChanged) {
      dispatch(setProjectDataSources(null));
    }

    fetchData(isFilterChanged ? getUpdatingDataSources() : undefined);
  }, [dispatch, fetchData, filtersDeps, getUpdatingDataSources, isLoading]);

  useEffect(() => {
    filterData();
  }, [page, filtersDeps]);

  useEffect(() => {
    if (isConfiguration) {
      filterData();
    }
  }, [currentConfig]);

  useEffect(() => {
    prevFilters.current = filters;
  }, [filters]);
};
